import { useSelector } from 'react-redux';
import { IS_CHOSEN_PUBLIC_FILE } from 'reactApp/appHelpers/configHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { getFilesWithSelectedFace } from 'reactApp/modules/faces/faces.selectors';
import { isMediaFolderAndGalleryEnabled } from 'reactApp/modules/file/utils';
import { isPublicUploadEnabled } from 'reactApp/modules/public/public.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getSort } from 'reactApp/modules/sort/sort.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageCurrentFolder, isFolderWithAuthor } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getGroupViewModes } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { RootState } from 'reactApp/store';
import { TOOLBAR_CONFIG } from 'reactApp/ui/Toolbar/Toolbar.config';
import { getSortItem, getViewItem, getViewItemByModes } from 'reactApp/ui/Toolbar/Toolbar.data';
import { ToolbarContextEnum, TToolbarItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import { FilterAuthorsDropdown } from 'reactApp/ui/Toolbar/ToolbarItem/FilterAuthorsDropdown/FilterAuthorsDropdown';

export const useFilterActions = ({ storage, toolbarType }: { storage: EStorageType; toolbarType?: ToolbarContextEnum }): TToolbarItem[] => {
    const filterActions: TToolbarItem[] = [];

    const settings = TOOLBAR_CONFIG[storage];

    const isPublicUpload = useSelector(isPublicUploadEnabled);
    const isSelectingMode = useSelector(SelectionsSelectors.isAnyIndexSelected);
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));
    const isMediaFolder = useSelector((state: RootState) => isMediaFolderAndGalleryEnabled(state, currentFolder));
    const filesWithFaces = useSelector(getFilesWithSelectedFace);
    const folderWithAuthors = useSelector(isFolderWithAuthor);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const facesIsOff = !filesWithFaces.length;
    const { isGallery, isQuotaCleaner, isPublic } = getStorage(storage);

    const sort = useSelector((state: RootState) => getSort(state, currentFolder, storage));
    const viewMode = useSelector((state: RootState) => SettingsSelectors.getViewByStorage(state, storage, isMediaFolder));

    const quotaCleanerGroupViewModes = useSelector((state: RootState) => getGroupViewModes(state));

    const showAuthorsFilter = settings?.authorSort && (folderWithAuthors || (isPublic && isPublicUpload)) && facesIsOff && !isAnonymous;
    const isRootAlbum = isAlbum(currentFolder) && currentFolder?.id === ROOT_FOLDER_ID;

    if ((isSelectingMode || IS_CHOSEN_PUBLIC_FILE) && toolbarType !== ToolbarContextEnum.pageContentContext) {
        return [];
    }

    if (showAuthorsFilter) {
        filterActions.push({
            id: 'author',
            Item: FilterAuthorsDropdown,
        });
    }

    if (settings?.view && !isRootAlbum) {
        const viewItem: TToolbarItem = isQuotaCleaner
            ? getViewItemByModes(viewMode, quotaCleanerGroupViewModes)
            : getViewItem({ viewMode, isGalleryAvailable: isGallery || isMediaFolder });

        // фильтр имеет смысл при > 1 вариантах отображения
        if ((viewItem.list?.length ?? 0) > 1) {
            filterActions.push(viewItem);
        }
    }

    if (settings?.sort && sort.type && sort.order && facesIsOff) {
        filterActions.push(getSortItem({ sortType: sort.type, sortOrder: sort.order }));
    }

    return filterActions;
};
